import { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared image lightbox with keyboard + swipe support.
// Usage:
//   <ImageLightbox images={[...]} initialIndex={0} onClose={() => ...} />
export default function ImageLightbox({ images, initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance && currentIndex < images.length - 1) setCurrentIndex((p) => p + 1);
    if (distance < -minSwipeDistance && currentIndex > 0) setCurrentIndex((p) => p - 1);
  };

  const goNext = (e) => { e.stopPropagation(); if (currentIndex < images.length - 1) setCurrentIndex((p) => p + 1); };
  const goPrev = (e) => { e.stopPropagation(); if (currentIndex > 0) setCurrentIndex((p) => p - 1); };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) setCurrentIndex((p) => p + 1);
      if (e.key === 'ArrowLeft' && currentIndex > 0) setCurrentIndex((p) => p - 1);
    };
    window.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', handleKey); document.body.style.overflow = prevOverflow; };
  }, [currentIndex, images.length, onClose]);

  if (!images || images.length === 0) return null;

  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="image-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white z-10 bg-zinc-800/50 hover:bg-zinc-700" data-testid="lightbox-close">
        <X size={24} />
      </button>

      <div className="absolute top-4 left-4 text-white font-mono text-sm bg-zinc-800/50 px-3 py-1">
        {currentIndex + 1} / {images.length}
      </div>

      {images.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button onClick={goPrev} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-zinc-800/50 text-white hover:bg-zinc-700 z-10">
              <ChevronLeft size={24} />
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button onClick={goNext} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-zinc-800/50 text-white hover:bg-zinc-700 z-10">
              <ChevronRight size={24} />
            </button>
          )}
        </>
      )}

      <div
        className="w-full h-full flex items-center justify-center p-4"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <img src={getImageUrl(images[currentIndex])} alt="" className="max-w-full max-h-full object-contain" />
      </div>

      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`w-2 h-2 rounded-full transition-colors ${idx === currentIndex ? 'bg-teal-500' : 'bg-zinc-600 hover:bg-zinc-500'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
